import { useNavigate } from 'react-router-dom';
import { Prisma } from '@prisma/client';
import { Button } from 'antd';
import { RuleObject } from 'antd/es/form';

import { Azure } from '@app/web-components';

import { useFindBundleByIdFetch } from '../../../bundles';
import { useFindSubscriptionFetch, useFindWorkerByEnterpriseIdFetch } from '../../data';

import { useSubscriptionPermissions } from './use-subscription-permissions';

export const useCreateSubscriptionValidations = () => {
  const navigate = useNavigate();
  const findBundleById = useFindBundleByIdFetch();
  const findWorkerByEnterpriseId = useFindWorkerByEnterpriseIdFetch();
  const findSubscription = useFindSubscriptionFetch();
  const { canEdit } = useSubscriptionPermissions();

  const subscriptionIsUniqueForUserRule = (worker?: Azure.WorkerOption) => {
    return (_rule: RuleObject, bundleId: number) => {
      return new Promise((resolve, reject) => {
        if (!bundleId) {
          // can't validate until a bundle is selected
          resolve('');
          return;
        }

        const workerId = worker?.value;

        if (!workerId) {
          // can't validate until a worker is selected
          resolve('');
          return;
        }

        const params: Prisma.SubscriptionFindManyArgs = {
          where: {
            bundleId,
            user: {
              enterpriseId: workerId,
            },
          },
          include: { bundle: true },
        };

        findSubscription(params)
          .then((result) => {
            if (result.count === 0) {
              resolve('');
            } else {
              try {
                reject(
                  <div style={{ marginBottom: 25 }}>
                    This user has already subscribed to this bundle.
                    {canEdit(result.data[0]) && (
                      <span>
                        {' '}
                        Please select another user or
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 3, fontWeight: 'bold' }}
                          onClick={() => navigate(`../${result.data[0].id}`)}
                        >
                          click here to edit the existing subscription
                        </Button>
                      </span>
                    )}
                  </div>,
                );
              } catch {
                reject('This user has already subscribed to this bundle.');
              }
            }
          })
          .catch(() => {
            reject('Error: unable to determine if user has already subscribed to this bundle.');
          });
      });
    };
  };

  const workerTypeMatchesBundleTypeRule = (worker?: Azure.WorkerOption) => {
    return (_rule: RuleObject, bundleId: number) => {
      return new Promise((resolve, reject) => {
        if (!bundleId || !worker?.value) {
          // can't validate until a bundle is selected
          return resolve('');
        }

        Promise.all([findBundleById(bundleId), findWorkerByEnterpriseId(worker.value)])
          .then(([bundle, worker]) => {
            if (!bundle || !worker) {
              throw new Error('Unable to find worker and determine their employee type.');
            } else if (worker.employeeType === bundle.employeeType) {
              resolve('');
            } else {
              reject('Worker type does not match the bundle employee type.');
            }
          })
          .catch((error) => {
            console.error(error);
            reject('Unable to determine if worker is the correct employee type.');
          });
      });
    };
  };

  return { navigate, subscriptionIsUniqueForUserRule, workerTypeMatchesBundleTypeRule };
};
